"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

const DEFAULT_IMAGES = [
	"/image/new-version/molding/injection-mold-tooling/when-to/1.webp",
	"/image/new-version/molding/injection-mold-tooling/when-to/2.webp",
];

/** 列表项：加粗标签 + 描述 */
const ListItem = ({ label, description }: { label: string; description: string }) => (
	<li className="flex gap-3 text-sm leading-relaxed text-black md:text-base">
		<CheckIcon />
		<span>
			<span className="font-bold text-black">{label}:</span> {description}
		</span>
	</li>
);

const CheckIcon = () => (
	<svg className="mt-0.5 h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="none" aria-hidden="true">
		<circle cx="10" cy="10" r="10" fill="#76B92D" />
		<path
			d="M6 10.5L8.5 13L14 7.5"
			stroke="white"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

export interface WhenToRapidVsProductionProps {
	/** 翻译命名空间，需包含 rapidSectionTitle、productionSectionTitle 及 rapidItem1Label/Desc～3、productionItem1Label/Desc～3、imageAlt1、imageAlt2 */
	translationNamespace: string;
	/** 右侧上下排列的图片路径，不传则用 when_to/1.webp、2.webp */
	imageUrls?: string[];
}

/**
 * When to Choose 区块：一段文案对应一张图，两行布局（Rapid 文案 | 图1；Production 文案 | 图2）
 * 样式：白底、绿色标题与勾选图标、每行左文右图对齐
 */
export default function WhenToRapidVsProduction({
	translationNamespace,
	imageUrls = DEFAULT_IMAGES,
}: WhenToRapidVsProductionProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 第一行：Rapid Tooling 文案 | 图1 */}
				<div className="grid grid-cols-1 gap-8 lg:grid-cols-[2fr_1fr] lg:items-start lg:gap-12 xl:gap-16">
					<div>
						<h3 className="mb-5 text-lg font-bold text-[#76B92D] sm:text-xl md:text-2xl">
							{t("rapidSectionTitle")}
						</h3>
						<ul className="space-y-4">
							<ListItem label={t("rapidItem1Label")} description={t("rapidItem1Desc")} />
							<ListItem label={t("rapidItem2Label")} description={t("rapidItem2Desc")} />
							<ListItem label={t("rapidItem3Label")} description={t("rapidItem3Desc")} />
						</ul>
					</div>
					<div className="relative aspect-[16/9] w-full max-w-xl overflow-hidden rounded-lg">
						<Image
							src={imageUrls[0]}
							alt={t("imageAlt1")}
							fill
							className="object-cover"
							sizes="(max-width: 1024px) 100vw, 45vw"
						/>
					</div>
				</div>

				{/* 第二行：Production Tooling 文案 | 图2 */}
				<div className="mt-10 grid grid-cols-1 gap-8 lg:mt-16 lg:grid-cols-[2fr_1fr] lg:items-start lg:gap-12 xl:gap-16">
					<div>
						<h3 className="mb-5 text-lg font-bold text-[#76B92D] sm:text-xl md:text-2xl">
							{t("productionSectionTitle")}
						</h3>
						<ul className="space-y-4">
							<ListItem label={t("productionItem1Label")} description={t("productionItem1Desc")} />
							<ListItem label={t("productionItem2Label")} description={t("productionItem2Desc")} />
							<ListItem label={t("productionItem3Label")} description={t("productionItem3Desc")} />
						</ul>
					</div>
					<div className="relative aspect-[16/9] w-full max-w-xl overflow-hidden rounded-lg">
						<Image
							src={imageUrls[1] ?? imageUrls[0]}
							alt={t("imageAlt2")}
							fill
							className="object-cover"
							sizes="(max-width: 1024px) 100vw, 45vw"
						/>
					</div>
				</div>
			</div>
		</section>
	);
}
